import React, { useEffect, useRef } from 'react';
import { Award, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import VanillaTilt from 'vanilla-tilt';

const achievements = [
    {
        id: 1,
        title: "Google Cloud Arcade – Swags & Rewards",
        subtitle: "Google Cloud Arcade 2025 (Cohort 1 & Cohort 2)",
        description: "Completed Google Cloud Arcade 2025 Cohort 1 & Cohort 2, finishing hands-on labs and challenges across core GCP services. Earned Google Cloud hoodie, stickers, and completion goodies as recognition for consistent learning and challenge completion.",
        tags: ["GCP", "Cloud Arcade", "Hands-on Labs", "Swags"],
        image: "/assets/google-cloud-arcade-swag.png",
        photoUrl: "https://example.com/google-cloud-arcade-photos"
    },
    {
        id: 2,
        title: "Neo4j Community Rewards",
        subtitle: "Neo4j Events & Challenges",
        description: "Participated in Neo4j graph database sessions and community challenges, receiving Neo4j T-shirt, stickers, and community swag for engagement and completion of activities focused on graph data and connected systems.",
        tags: ["Neo4j", "Graph Databases", "Community", "Swags"],
        image: "/assets/neo4j-swag.png",
        photoUrl: "https://example.com/neo4j-swag-photos"
    },
    {
        id: 3,
        title: "Azure Developer Day – Event Badge",
        subtitle: "Microsoft – Azure Developer Day",
        description: "Attended Microsoft Azure Developer Day, gaining exposure to Azure services, live demos, and hands-on sessions while networking with engineers and developers. Received an official event badge as recognition of participation.",
        tags: ["Azure", "Microsoft", "Event Badge", "Community"],
        // Using the actual Azure event badge
        image: "/assets/azure-developer-day-badge.png",
        photoUrl: "https://example.com/azure-developer-day-photos"
    }
];

const AchievementsSection = () => {
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });
    const cardRefs = useRef([]);

    useEffect(() => {
        cardRefs.current.forEach((card) => {
            if (card) {
                VanillaTilt.init(card, {
                    max: 10,
                    speed: 400,
                    glare: true,
                    'max-glare': 0.3
                });
            }
        });

        return () => {
            cardRefs.current.forEach((card) => {
                if (card && card.vanillaTilt) {
                    card.vanillaTilt.destroy();
                }
            });
        };
    }, []);

    return (
        <section id="achievements" className="min-h-screen flex items-center justify-center py-20 relative" ref={ref}>
            <div className="container mx-auto px-6">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
                        <span className="text-cyan-400">Achievements</span>
                    </h2>
                    <p className="text-gray-400 text-lg">Recognitions, swags, and community events from my cloud & DevOps journey</p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {achievements.map((achievement, index) => (
                        <motion.div
                            key={achievement.id}
                            ref={(el) => (cardRefs.current[index] = el)}
                            className="group bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden hover:border-cyan-400/50 transition-all duration-300 glow-on-hover"
                            initial={{ opacity: 0, y: 50 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                        >
                            <div className="relative overflow-hidden">
                                <img
                                    src={achievement.image}
                                    alt={achievement.title}
                                    width={800}
                                    height={224}
                                    loading="lazy"
                                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <h3 className="text-2xl font-bold text-white">{achievement.title}</h3>
                                    <p className="text-cyan-400 text-sm mt-1 font-medium">{achievement.subtitle}</p>
                                </div>
                                <p className="text-gray-300 leading-relaxed">{achievement.description}</p>
                                <div className="flex flex-wrap gap-2">
                                    {achievement.tags.map((tag, idx) => (
                                        <span
                                            key={idx}
                                            className="px-3 py-1 bg-gray-700/50 text-cyan-400 rounded-full text-sm font-medium"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <a
                                        href={achievement.photoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-4 py-2 border-2 border-cyan-400 text-cyan-400 rounded-md text-sm font-medium transition-all duration-300 hover:bg-cyan-400 hover:text-black hover:shadow-lg hover:shadow-cyan-400/50 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-gray-900"
                                    >
                                        <ImageIcon size={16} className="mr-2" />
                                        View Photos
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AchievementsSection;
